import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ArrowUpRight, ArrowLeft, Phone, MessageCircle, Languages, Globe,
  ShieldCheck, Lock, FileCheck, Clock, AlertTriangle, Building2, Send, User,
  CheckCircle2, Sparkles, MapPin, BadgeCheck, Plus, Minus
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { translations } from '../i18n/translations';
import { getPublicApiBaseUrl } from '../utils/publicApi';
import { trackGoogleAdsFormConversion } from '../utils/googleAds';
import { BRAND_ICON_SRC } from '../constants/brand';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import { loadGoogleMaps } from '@/utils/loadGoogleMaps';

const RecoveryLanding = () => {
  const [language, setLanguage] = useState(() => localStorage.getItem('autogoLanguage') || 'es');
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const [form, setForm] = useState({
    company: '', name: '', email: '', phone: '',
    caseType: '', location: '', urgency: '', notes: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const t = translations[language];
  const r = t.recoveryLanding;
  const revealRefs = useRef([]);

  useEffect(() => {
    document.title = r.meta.title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', r.meta.description);
  }, [r]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add('is-visible')),
      { threshold: 0.12 }
    );
    revealRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    loadGoogleMaps().catch(() => {});
  }, []);

  const addReveal = (el) => {
    if (el && !revealRefs.current.includes(el)) revealRefs.current.push(el);
  };

  const toggleLanguage = () => {
    setLanguage((prev) => {
      const next = prev === 'es' ? 'en' : 'es';
      localStorage.setItem('autogoLanguage', next);
      return next;
    });
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${getPublicApiBaseUrl()}/api/recovery-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error();
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Recovery Request',
          content_category: 'Vehicle Recovery',
          value: 0, currency: 'EUR'
        });
      }
      trackGoogleAdsFormConversion();
      toast({ title: r.form.successTitle, description: r.form.successDescription });
      setSent(true);
    } catch {
      toast({ title: r.form.errorTitle, description: r.form.errorDescription, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleWhatsApp = () => {
    const text = language === 'es'
      ? `Hola! Necesito activar un caso de recuperación de vehículo.${form.company ? `\n\nEmpresa: ${form.company}` : ''}${form.caseType ? `\nCaso: ${form.caseType}` : ''}`
      : `Hello! I need to activate a vehicle recovery case.${form.company ? `\n\nCompany: ${form.company}` : ''}${form.caseType ? `\nCase: ${form.caseType}` : ''}`;
    if (window.fbq) window.fbq('track', 'Contact', { content_name: 'Recovery WhatsApp', content_category: 'Vehicle Recovery' });
    window.open(`https://wa.me/34930491501?text=${encodeURIComponent(text)}`, '_blank');
  };

  const forWhoIcons = [Building2, BadgeCheck, FileCheck, ShieldCheck];
  const whyIcons = [Lock, Globe, ShieldCheck, FileCheck];

  return (
    <div className="relative min-h-screen text-[#F8FAFC] overflow-x-hidden" style={{ background: '#060B14' }}>
      {/* Ambient bg */}
      <div className="ag-grain" />
      <div className="fixed inset-0 ag-grid-bg opacity-[0.35] pointer-events-none" />
      <div className="ag-radial-spot" style={{ top: '-200px', left: '-150px', width: '600px', height: '600px', background: 'rgba(255, 91, 91, 0.16)' }} />
      <div className="ag-radial-spot" style={{ top: '20%', right: '-200px', width: '700px', height: '700px', background: 'rgba(0, 242, 96, 0.12)' }} />

      {/* Top banner */}
      <div className="fixed top-0 inset-x-0 z-50 ag-glass-strong border-b border-white/5 text-center py-2 text-[11px] sm:text-xs tracking-[0.18em] uppercase font-mono" data-testid="recovery-top-banner">
        <span className="inline-flex items-center gap-2 text-[#94A3B8]">
          <span className="inline-flex h-2 w-2 rounded-full bg-[#ff5b5b] ag-pulse" />
          {language === 'es' ? 'Operaciones 24/7 · Cobertura UE · NDA garantizado' : '24/7 Operations · EU Coverage · NDA guaranteed'}
        </span>
      </div>

      {/* Header */}
      <header className={`fixed top-[34px] sm:top-[36px] inset-x-0 z-50 transition-all duration-300 ag-glass-strong ${scrolled ? 'border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.35)]' : 'border-b border-white/5'}`} data-testid="recovery-header">
        <div className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between h-16 md:h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="recovery-logo-link">
            <div className="relative w-10 h-10 rounded-xl overflow-hidden border border-white/10 ag-glass-strong flex items-center justify-center">
              <img src={BRAND_ICON_SRC} alt="AutoGo" className="w-7 h-7 object-contain" />
              <div className="absolute inset-0 bg-gradient-to-br from-[#00F260]/20 to-transparent" />
            </div>
            <div className="leading-none">
              <div className="font-display text-lg md:text-xl font-extrabold tracking-tight">AUTOGO</div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-[#ff5b5b] mt-1">RECOVERY</div>
            </div>
          </Link>

          <Link to="/" className="hidden md:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#94A3B8] hover:text-white transition-colors" data-testid="recovery-back-home">
            <ArrowLeft className="w-3.5 h-3.5" />
            {r.backHome}
          </Link>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggleLanguage}
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-widest text-[#94A3B8] hover:text-white border border-white/10 rounded-full hover:border-[#00F260]/40 transition-all"
              data-testid="recovery-language-toggle"
            >
              <Languages className="w-3.5 h-3.5" />
              {language}
            </button>
            <a
              href="#recovery-form"
              className="group relative inline-flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-[#ff5b5b] text-[#060B14] font-semibold text-xs sm:text-sm rounded-full hover:bg-white transition-colors"
              data-testid="recovery-header-cta"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {r.hero.ctaPrimary}
              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-[140px] md:pt-[170px] pb-16 md:pb-24 px-5 md:px-10" data-testid="recovery-hero">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 ag-animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#ff5b5b]/10 border border-[#ff5b5b]/40 rounded-full text-[10px] md:text-[11px] tracking-[0.25em] font-mono text-[#ff5b5b] mb-6">
              <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#ff5b5b] ag-pulse" />
              {r.hero.badge}
            </div>
            <h1 className="font-display text-[44px] sm:text-6xl lg:text-[80px] xl:text-[92px] font-extrabold leading-[0.95] tracking-tight">
              <span className="block">{r.hero.title}</span>
              <span className="block ag-shine-text">{r.hero.titleAccent}</span>
            </h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-[#94A3B8] leading-relaxed">{r.hero.description}</p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#recovery-form" className="group inline-flex items-center gap-2 bg-[#ff5b5b] text-[#060B14] font-semibold px-6 py-3.5 rounded-full hover:bg-white transition-colors" data-testid="recovery-hero-cta-primary">
                <AlertTriangle className="w-4 h-4" />
                {r.hero.ctaPrimary}
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <button onClick={handleWhatsApp} className="inline-flex items-center gap-2 bg-transparent border border-white/20 text-white px-6 py-3.5 rounded-full hover:border-[#00F260] hover:text-[#00F260] transition-colors" data-testid="recovery-hero-cta-secondary">
                <MessageCircle className="w-4 h-4" />
                {r.hero.ctaSecondary}
              </button>
            </div>

            <div className="flex flex-wrap gap-2.5 mt-7">
              {r.hero.trust.map((label, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 ag-glass rounded-full text-xs text-[#94A3B8]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-[#00F260]" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Right: Case file mockup */}
          <div className="lg:col-span-5 ag-animate-fade-up" style={{ animationDelay: '.15s' }}>
            <CaseFileMockup language={language} />
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="relative z-10 border-y border-white/10 ag-glass" data-testid="recovery-stats">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
          {r.stats.map((s, i) => (
            <div key={i} className="px-4 md:px-8 py-7 md:py-10">
              <div className="font-display text-3xl md:text-5xl font-extrabold text-white">{s.v}</div>
              <div className="mt-1 text-xs md:text-sm text-[#94A3B8]">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* For Whom */}
      <section className="relative pt-12 md:pt-20 pb-12 md:pb-16 px-5 md:px-10" data-testid="recovery-forwho">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.25em] text-[#ff5b5b] mb-4">{r.forWho.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
              {r.forWho.title}<br />
              <span className="text-[#94A3B8]">{r.forWho.titleAccent}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {r.forWho.list.map((item, i) => {
              const Icon = forWhoIcons[i];
              return (
                <div key={i} ref={addReveal} className="ag-reveal ag-card ag-hover-lift p-6 md:p-7 relative overflow-hidden">
                  <div className="w-11 h-11 rounded-xl bg-[#ff5b5b]/10 border border-[#ff5b5b]/30 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-[#ff5b5b]" />
                  </div>
                  <h3 className="font-display text-xl font-bold">{item.title}</h3>
                  <p className="mt-2 text-sm text-[#94A3B8]">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Cases */}
      <section className="relative pt-8 md:pt-12 pb-12 md:pb-20 px-5 md:px-10" data-testid="recovery-cases">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal mb-10 md:mb-14 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <div className="font-mono text-xs tracking-[0.25em] text-[#ff5b5b] mb-4">{r.cases.tagline}</div>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">{r.cases.title}</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {r.cases.items.map((c, i) => (
              <div key={i} ref={addReveal} className="ag-reveal ag-card ag-hover-lift p-6 md:p-8 relative overflow-hidden border border-[#ff5b5b]/10">
                <div className="absolute -top-16 -right-16 w-32 h-32 rounded-full bg-[#ff5b5b]/10 blur-3xl pointer-events-none" />
                <div className="flex items-center gap-3 mb-4">
                  <span className="px-2.5 py-1 bg-[#ff5b5b]/10 border border-[#ff5b5b]/30 rounded text-[10px] font-mono tracking-[0.2em] text-[#ff5b5b]">{c.tag}</span>
                  <div className="h-px flex-1 bg-white/5" />
                  <span className="font-mono text-[10px] tracking-widest text-[#475569]">#{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-bold leading-tight">{c.title}</h3>
                <p className="mt-3 text-sm md:text-base text-[#94A3B8]">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="relative pt-12 md:pt-16 pb-12 md:pb-20 px-5 md:px-10 border-t border-white/5" data-testid="recovery-how">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{r.how.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">
              {r.how.title}<br />
              <span className="ag-shine-text">{r.how.titleAccent}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {r.how.steps.map((step, i) => {
              const stepIcons = [Phone, MapPin, ShieldCheck, BadgeCheck];
              const Icon = stepIcons[i];
              // Color progression cyan→teal→lime→neon green
              const accents = ['#22D3EE', '#1B93A4', '#65E36C', '#00F260'];
              const accent = accents[i];
              const isLast = i === r.how.steps.length - 1;
              return (
                <div key={i} ref={addReveal} className="ag-reveal relative">
                  {/* Connector line to next card */}
                  {!isLast && (
                    <div className="hidden lg:block absolute top-16 -right-3 w-6 h-px bg-gradient-to-r from-white/15 to-transparent z-10" />
                  )}
                  <div className="ag-card ag-hover-lift p-6 md:p-7 relative overflow-hidden h-full" style={{ borderColor: `${accent}22` }}>
                    {/* Top gradient accent bar */}
                    <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg, transparent, ${accent}, transparent)` }} />
                    {/* Subtle accent halo */}
                    <div className="absolute -top-20 -right-12 w-36 h-36 rounded-full blur-3xl pointer-events-none" style={{ background: `${accent}1a` }} />

                    <div className="relative">
                      {/* Icon + step indicator */}
                      <div className="flex items-center justify-between mb-5">
                        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${accent}1a`, border: `1px solid ${accent}55` }}>
                          <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={2} />
                        </div>
                        <div className="font-mono text-[10px] tracking-[0.3em]" style={{ color: accent }}>
                          {step.num}<span className="text-[#475569]">/04</span>
                        </div>
                      </div>

                      <h3 className="font-display text-xl md:text-2xl font-bold leading-tight">{step.title}</h3>
                      <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">{step.desc}</p>

                      {/* Bottom checkpoint indicator */}
                      <div className="mt-6 pt-4 border-t border-white/5 flex items-center gap-2">
                        {[0, 1, 2, 3].map((dot) => (
                          <span
                            key={dot}
                            className="h-1 rounded-full transition-all"
                            style={{
                              flex: dot === i ? 2 : 1,
                              background: dot <= i ? accent : 'rgba(255,255,255,0.06)'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="relative pt-8 md:pt-12 pb-12 md:pb-20 px-5 md:px-10" data-testid="recovery-why">
        <div className="max-w-7xl mx-auto">
          <div ref={addReveal} className="ag-reveal max-w-3xl mb-10 md:mb-14">
            <div className="font-mono text-xs tracking-[0.25em] text-[#ff5b5b] mb-4">{r.whyUs.tagline}</div>
            <h2 className="font-display text-4xl md:text-6xl font-extrabold leading-[0.95] tracking-tight">{r.whyUs.title}</h2>
          </div>

          {(() => {
            const accents = ['#00F260', '#22D3EE', '#ff5b5b', '#65E36C'];
            const inlineStats = [
              language === 'es' ? '27 países' : '27 countries',
              language === 'es' ? '100% trazado' : '100% traced',
              language === 'es' ? 'NDA' : 'NDA',
              language === 'es' ? 'Recuperación <48h' : 'Recovery <48h'
            ];
            // Order: Red europea, Legal trazado, Confidencialidad, Coste cerrado
            const order = [1, 0, 2, 3];
            return (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                {order.map((origIdx, displayIdx) => {
                  const p = r.whyUs.points[origIdx];
                  const Icon = whyIcons[origIdx];
                  const accent = accents[displayIdx];
                  const stat = inlineStats[displayIdx];
                  return (
                    <div
                      key={origIdx}
                      ref={addReveal}
                      className="ag-reveal ag-glass rounded-2xl p-5 md:p-6 flex items-start gap-4 relative overflow-hidden border"
                      style={{ borderColor: `${accent}26` }}
                    >
                      <div
                        className="absolute -top-12 -right-10 w-28 h-28 rounded-full blur-3xl pointer-events-none"
                        style={{ background: `${accent}1a` }}
                      />
                      <div
                        className="w-11 h-11 shrink-0 rounded-xl flex items-center justify-center relative z-10"
                        style={{ background: `${accent}1a`, border: `1px solid ${accent}55` }}
                      >
                        <Icon className="w-5 h-5" style={{ color: accent }} strokeWidth={2} />
                      </div>
                      <div className="relative z-10 flex-1">
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <h3 className="font-display text-lg md:text-xl font-bold leading-tight">{p.title}</h3>
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-[10px] tracking-[0.15em] font-semibold"
                            style={{ background: `${accent}1a`, color: accent, border: `1px solid ${accent}40` }}
                          >
                            {stat}
                          </span>
                        </div>
                        <p className="mt-1.5 text-sm text-[#94A3B8] leading-relaxed">{p.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* FAQ */}
      <section className="relative pt-8 md:pt-12 pb-12 md:pb-20 px-5 md:px-10 border-t border-white/5" data-testid="recovery-faq">
        <div className="max-w-4xl mx-auto">
          <div ref={addReveal} className="ag-reveal mb-8 md:mb-12 text-center">
            <div className="font-mono text-xs tracking-[0.25em] text-[#00F260] mb-4">{r.faq.tagline}</div>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-[0.95] tracking-tight">{r.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {r.faq.items.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={i} className={`ag-glass rounded-xl border transition-colors ${isOpen ? 'border-[#00F260]/40' : 'border-white/5'}`}>
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left"
                    data-testid={`recovery-faq-${i}`}
                  >
                    <span className="font-display text-base md:text-lg font-bold">{f.q}</span>
                    {isOpen ? <Minus className="w-4 h-4 text-[#00F260] shrink-0" /> : <Plus className="w-4 h-4 text-[#94A3B8] shrink-0" />}
                  </button>
                  {isOpen && (
                    <div className="px-5 md:px-6 pb-5 md:pb-6 text-sm md:text-base text-[#94A3B8] leading-relaxed">{f.a}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="recovery-form" className="relative pt-8 md:pt-12 pb-20 md:pb-28 px-5 md:px-10" data-testid="recovery-form-section">
        <div className="max-w-5xl mx-auto">
          <div ref={addReveal} className="ag-reveal relative rounded-[28px] overflow-hidden border border-[#ff5b5b]/20" style={{ background: 'linear-gradient(180deg, #060B14 0%, #0A0710 100%)' }}>
            <div className="absolute -top-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#ff5b5b]/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#00F260]/10 blur-3xl pointer-events-none" />

            <div className="relative p-7 md:p-12 lg:p-16">
              <div className="grid lg:grid-cols-12 gap-8 md:gap-12">
                <div className="lg:col-span-5">
                  <div className="font-mono text-xs tracking-[0.25em] text-[#ff5b5b] mb-4">{r.form.tagline}</div>
                  <h2 className="font-display text-4xl md:text-5xl font-extrabold leading-[0.95] tracking-tight">{r.form.title}</h2>
                  <p className="mt-4 text-[#94A3B8]">{r.form.subtitle}</p>
                  <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 ag-glass rounded-full text-[10px] tracking-[0.2em] font-mono text-[#00F260]">
                    <Lock className="w-3.5 h-3.5" />
                    {r.form.nda}
                  </div>

                  {/* Direct contact block */}
                  <div className="mt-7 pt-7 border-t border-white/5" data-testid="recovery-direct-contact">
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#475569] mb-4">
                      {language === 'es' ? '¿PREFIERES ESCRIBIR O LLAMAR?' : 'PREFER EMAIL OR PHONE?'}
                    </div>
                    <div className="space-y-2.5">
                      <a href="tel:+34930491501" className="group flex items-center gap-3 p-3.5 rounded-xl ag-glass border border-white/5 hover:border-[#ff5b5b]/50 transition-colors" data-testid="recovery-direct-phone">
                        <div className="w-10 h-10 rounded-lg bg-[#ff5b5b]/10 border border-[#ff5b5b]/30 flex items-center justify-center">
                          <Phone className="w-4 h-4 text-[#ff5b5b]" />
                        </div>
                        <div className="flex-1">
                          <div className="font-mono text-[9px] tracking-[0.22em] text-[#475569]">{language === 'es' ? 'LLAMADA DIRECTA' : 'CALL DIRECT'}</div>
                          <div className="font-display text-sm md:text-base font-semibold text-white mt-0.5">+34 930 49 15 01</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#475569] group-hover:text-[#ff5b5b] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </a>
                      <a href="mailto:hello@autogodelivery.com" className="group flex items-center gap-3 p-3.5 rounded-xl ag-glass border border-white/5 hover:border-[#00F260]/40 transition-colors" data-testid="recovery-direct-email">
                        <div className="w-10 h-10 rounded-lg bg-[#00F260]/10 border border-[#00F260]/30 flex items-center justify-center">
                          <Send className="w-4 h-4 text-[#00F260]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[9px] tracking-[0.22em] text-[#475569]">{language === 'es' ? 'ESCRÍBENOS' : 'EMAIL US'}</div>
                          <div className="font-display text-sm md:text-base font-semibold text-white mt-0.5 break-all">hello@autogodelivery.com</div>
                        </div>
                        <ArrowUpRight className="w-4 h-4 text-[#475569] group-hover:text-[#00F260] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="lg:col-span-7">
                  <div className="ag-glass-strong rounded-2xl p-6 md:p-7 relative overflow-hidden border border-[#ff5b5b]/20">
                    <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#ff5b5b] via-[#ff8b5b] to-[#ff5b5b]" />
                    {sent ? (
                      <div className="relative z-10 text-center py-10" data-testid="recovery-form-success">
                        <div className="w-16 h-16 mx-auto rounded-full bg-[#00F260] flex items-center justify-center mb-5">
                          <CheckCircle2 className="w-8 h-8 text-[#060B14]" strokeWidth={3} />
                        </div>
                        <h3 className="font-display text-2xl font-bold">{r.form.successTitle}</h3>
                        <p className="mt-3 text-sm text-[#94A3B8] max-w-xs mx-auto">{r.form.successDescription}</p>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-3 relative z-10" data-testid="recovery-form">
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Field icon={Building2} label={r.form.company} name="company" value={form.company} onChange={handleChange} placeholder={r.form.companyPlaceholder} required testId="rec-company" />
                          <Field icon={User} label={r.form.name} name="name" value={form.name} onChange={handleChange} placeholder={r.form.namePlaceholder} required testId="rec-name" />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-3">
                          <Field icon={Send} label={r.form.email} name="email" value={form.email} onChange={handleChange} placeholder={r.form.emailPlaceholder} type="email" required testId="rec-email" />
                          <Field icon={Phone} label={r.form.phone} name="phone" value={form.phone} onChange={handleChange} placeholder={r.form.phonePlaceholder} required testId="rec-phone" />
                        </div>
                        <Field icon={AlertTriangle} label={r.form.caseType} name="caseType" value={form.caseType} onChange={handleChange} placeholder={r.form.caseTypePlaceholder} testId="rec-casetype" />
                        <div data-testid="rec-location">
                          <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{r.form.location}</label>
                          <AddressAutocomplete
                            embedOnDark
                            value={form.location}
                            onChange={(data) => setForm({ ...form, location: data.address })}
                            placeholder={r.form.locationPlaceholder}
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{r.form.urgency}</label>
                          <div className="flex flex-wrap gap-2">
                            {r.form.urgencyOptions.map((opt, i) => (
                              <button
                                type="button"
                                key={i}
                                onClick={() => setForm({ ...form, urgency: opt })}
                                className={`px-4 py-2 rounded-full text-xs border transition-colors ${form.urgency === opt ? 'bg-[#ff5b5b]/15 border-[#ff5b5b] text-[#ff5b5b]' : 'border-white/10 text-[#94A3B8] hover:border-white/30 hover:text-white'}`}
                                data-testid={`rec-urgency-${i}`}
                              >
                                {opt}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div>
                          <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{r.form.notes}</label>
                          <textarea
                            name="notes"
                            value={form.notes}
                            onChange={handleChange}
                            placeholder={r.form.notesPlaceholder}
                            rows={3}
                            className="ag-input resize-none"
                            data-testid="rec-notes"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={sending}
                          className="group w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#ff5b5b] hover:bg-white text-[#060B14] font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60"
                          data-testid="rec-submit"
                        >
                          {sending ? '...' : r.form.submit}
                          <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </button>
                        <button
                          type="button"
                          onClick={handleWhatsApp}
                          className="w-full inline-flex items-center justify-center gap-2 text-[#94A3B8] hover:text-[#00F260] py-2 text-xs font-mono uppercase tracking-widest transition-colors"
                          data-testid="rec-whatsapp"
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          {r.form.whatsapp}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative pt-12 md:pt-20 pb-10 px-5 md:px-10 border-t border-white/5 overflow-hidden" data-testid="recovery-footer">
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl ag-glass-strong overflow-hidden flex items-center justify-center">
                  <img src={BRAND_ICON_SRC} alt="AutoGo" className="w-7 h-7" />
                </div>
                <div className="leading-none">
                  <div className="font-display text-xl font-extrabold">AUTOGO</div>
                  <div className="font-mono text-[9px] tracking-[0.25em] text-[#ff5b5b] mt-1">RECOVERY</div>
                </div>
              </div>
              <p className="text-[#94A3B8] max-w-sm text-sm">{t.footer.description}</p>
              <button onClick={handleWhatsApp} className="mt-6 inline-flex items-center gap-2 bg-[#ff5b5b] text-[#060B14] font-semibold px-5 py-2.5 rounded-full hover:bg-white transition-colors text-sm" data-testid="recovery-footer-whatsapp">
                <MessageCircle className="w-4 h-4" />
                {t.footer.whatsapp}
              </button>
            </div>

            <div className="md:col-span-3">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.quickLinks}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><Link to="/" className="hover:text-white transition-colors">{r.backHome}</Link></li>
                <li><a href="#recovery-form" className="hover:text-white transition-colors">{r.hero.ctaPrimary}</a></li>
                <li><a href="#recovery-form" className="hover:text-white transition-colors">{r.form.tagline}</a></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.contact}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><a href="tel:+34930491501" className="hover:text-white" data-testid="recovery-footer-phone">+34 930 49 15 01</a></li>
                <li><a href="mailto:hello@autogodelivery.com" className="hover:text-white break-all" data-testid="recovery-footer-email">hello@autogodelivery.com</a></li>
                <li data-testid="recovery-footer-address">Carrer de Lepant 270, 08013, Barcelona, España</li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <div className="font-mono text-xs tracking-[0.25em] text-[#475569] mb-4">{t.footer.legal}</div>
              <ul className="space-y-2.5 text-sm text-[#94A3B8]">
                <li><Link to="/politica-de-privacidad" className="hover:text-white">{t.footer.privacyPolicy}</Link></li>
                <li><Link to="/politica-de-cookies" className="hover:text-white">{t.footer.cookiesPolicy}</Link></li>
              </ul>
            </div>
          </div>

          {/* Massive watermark + bottom */}
          <div className="relative pt-8 border-t border-white/5">
            <div aria-hidden className="font-display font-extrabold tracking-tight text-[18vw] leading-none text-white/[0.03] select-none pointer-events-none whitespace-nowrap text-center -mb-12 md:-mb-20">
              AUTOGO
            </div>
            <div className="relative flex flex-col items-center justify-center text-xs text-[#475569] z-10 text-center">
              <div>{t.footer.rights}</div>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] hover:scale-105 transition-transform shadow-[0_8px_30px_rgba(37,211,102,0.4)] flex items-center justify-center"
        aria-label="WhatsApp"
        data-testid="recovery-whatsapp-float"
      >
        <MessageCircle className="w-6 h-6 text-white" />
      </button>
    </div>
  );
};

const Field = ({ icon: Icon, label, name, value, onChange, placeholder, type = 'text', required = false, testId }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#475569] mb-1.5 font-mono">{label}</label>
    <div className="relative">
      {Icon && <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />}
      <input
        className="ag-input"
        style={{ paddingLeft: '2.5rem' }}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        data-testid={testId}
      />
    </div>
  </div>
);

const CaseFileMockup = ({ language }) => {
  const isEs = language === 'es';
  return (
    <div className="ag-glass-strong rounded-2xl p-5 md:p-6 relative overflow-hidden border border-[#ff5b5b]/20">
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-[#ff5b5b]/15 blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#ff5b5b] via-[#ff8b5b] to-[#ff5b5b]" />
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-2 w-2 rounded-full bg-[#ff5b5b] ag-pulse" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-[#ff5b5b]">{isEs ? 'EXPEDIENTE ABIERTO' : 'OPEN CASE FILE'}</span>
          </div>
          <span className="font-mono text-[10px] tracking-widest text-[#475569]">#R-2026-{Math.floor(Math.random() * 900) + 100}</span>
        </div>

        <div className="space-y-3">
          <Row label={isEs ? 'Cliente' : 'Client'} value={isEs ? 'Financiera Auto S.A.' : 'Auto Finance Co.'} />
          <Row label={isEs ? 'Tipo de caso' : 'Case type'} value={isEs ? 'Vehículo no devuelto · Alquiler vencido' : 'Unreturned vehicle · Expired rental'} />
          <Row label={isEs ? 'Vehículo' : 'Vehicle'} value="BMW X3 · 2023" />
          <Row label={isEs ? 'Última ubicación' : 'Last location'} value={isEs ? 'Lyon, FR · hace 2h' : 'Lyon, FR · 2h ago'} />
        </div>

        <div className="mt-5 pt-5 border-t border-white/5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-widest text-[#475569]">{isEs ? 'PROGRESO' : 'PROGRESS'}</span>
            <span className="font-mono text-[10px] text-[#00F260]">75%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#ff5b5b] via-[#ff8b5b] to-[#00F260]" style={{ width: '75%' }} />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-1.5 text-[9px] font-mono tracking-wider text-center">
            {[isEs ? 'ACTIV.' : 'ACT', isEs ? 'LOCAL.' : 'LOC', isEs ? 'RECUP.' : 'REC', isEs ? 'ENTREGA' : 'DEL'].map((s, i) => (
              <div key={i} className={`py-1.5 rounded ${i < 3 ? 'bg-[#00F260]/10 text-[#00F260]' : 'bg-white/5 text-[#475569]'}`}>{s}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 px-3 py-2 rounded-lg bg-white/[0.02] border border-white/5">
    <span className="font-mono text-[10px] tracking-widest text-[#475569]">{label}</span>
    <span className="text-xs md:text-sm text-white font-medium">{value}</span>
  </div>
);

/* Stylized Europe coverage with pulsing city dots */
const EuropeDotsMap = ({ accent, language }) => {
  // Approximate coordinates of major European cities on a 280x180 viewbox
  const cities = [
    { name: 'Madrid', x: 70, y: 130 },
    { name: 'Barcelona', x: 95, y: 125 },
    { name: 'Lisboa', x: 30, y: 140 },
    { name: 'Paris', x: 120, y: 80 },
    { name: 'Lyon', x: 130, y: 95 },
    { name: 'Milan', x: 145, y: 100 },
    { name: 'Roma', x: 155, y: 130 },
    { name: 'Berlin', x: 165, y: 60 },
    { name: 'Amsterdam', x: 135, y: 60 },
    { name: 'Bruselas', x: 130, y: 70 },
    { name: 'Viena', x: 175, y: 90 },
    { name: 'Praga', x: 170, y: 75 },
    { name: 'Varsovia', x: 195, y: 60 },
    { name: 'Budapest', x: 190, y: 95 },
    { name: 'Bucarest', x: 215, y: 105 },
    { name: 'Atenas', x: 205, y: 150 },
    { name: 'Estocolmo', x: 180, y: 25 },
    { name: 'Oslo', x: 155, y: 25 },
    { name: 'Copenhague', x: 160, y: 45 },
    { name: 'Helsinki', x: 210, y: 20 },
    { name: 'Dublin', x: 70, y: 60 },
    { name: 'Londres', x: 100, y: 60 },
    { name: 'Zurich', x: 140, y: 90 },
    { name: 'Sofia', x: 205, y: 120 },
    { name: 'Zagreb', x: 175, y: 105 },
    { name: 'Riga', x: 205, y: 40 },
    { name: 'Tallin', x: 215, y: 30 }
  ];
  return (
    <div className="mt-6 relative w-full" style={{ aspectRatio: '280/180', maxWidth: '460px' }}>
      <svg viewBox="0 0 280 180" className="w-full h-full">
        <defs>
          <radialGradient id="map-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor={accent} stopOpacity="0.18" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </radialGradient>
          <filter id="dot-glow">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Background coverage halo */}
        <ellipse cx="140" cy="90" rx="115" ry="75" fill="url(#map-glow)" />

        {/* Subtle grid */}
        {Array.from({ length: 7 }).map((_, k) => (
          <line key={`h${k}`} x1="0" y1={25 * (k + 1)} x2="280" y2={25 * (k + 1)} stroke="white" strokeOpacity="0.04" strokeWidth="0.4" />
        ))}
        {Array.from({ length: 11 }).map((_, k) => (
          <line key={`v${k}`} x1={25 * (k + 1)} y1="0" x2={25 * (k + 1)} y2="180" stroke="white" strokeOpacity="0.04" strokeWidth="0.4" />
        ))}

        {/* Connection lines from Spain hub (Madrid) to a few key cities */}
        {[
          { x: 120, y: 80 }, // Paris
          { x: 165, y: 60 }, // Berlin
          { x: 155, y: 130 }, // Roma
          { x: 195, y: 60 }, // Varsovia
          { x: 210, y: 20 } // Helsinki
        ].map((c, k) => (
          <line key={`line${k}`} x1="70" y1="130" x2={c.x} y2={c.y} stroke={accent} strokeOpacity="0.18" strokeWidth="0.5" strokeDasharray="2 3" />
        ))}

        {/* City dots */}
        {cities.map((c, k) => {
          const isHub = c.name === 'Madrid' || c.name === 'Barcelona';
          return (
            <g key={c.name}>
              {isHub && (
                <circle cx={c.x} cy={c.y} r="3.5" fill="none" stroke={accent} strokeOpacity="0.5" className="ag-pulse">
                  <animate attributeName="r" values="3.5;7;3.5" dur="2.4s" repeatCount="indefinite" begin={`${k * 0.1}s`} />
                  <animate attributeName="stroke-opacity" values="0.6;0;0.6" dur="2.4s" repeatCount="indefinite" begin={`${k * 0.1}s`} />
                </circle>
              )}
              <circle
                cx={c.x}
                cy={c.y}
                r={isHub ? 2 : 1.4}
                fill={accent}
                opacity={isHub ? 1 : 0.7}
                filter={isHub ? 'url(#dot-glow)' : undefined}
              />
            </g>
          );
        })}

        {/* Hub label */}
        <text x="70" y="143" fill="white" fontSize="6" fontFamily="JetBrains Mono, monospace" letterSpacing="0.5" opacity="0.7">MADRID · HQ</text>
      </svg>
      {/* Legend */}
      <div className="absolute bottom-1 right-1 flex items-center gap-3 font-mono text-[9px] tracking-widest text-[#475569]">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
          {language === 'es' ? 'CIUDAD ACTIVA' : 'ACTIVE CITY'}
        </span>
      </div>
    </div>
  );
};

export default RecoveryLanding;
